import { QueryDocumentSnapshot } from '@angular/fire/firestore';
import {
  collection,
  doc,
  DocumentChangeType,
  DocumentData,
  DocumentReference,
  Firestore,
  FirestoreDataConverter,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  Query as FirestoreQuery,
  query,
  QueryConstraint,
  QuerySnapshot,
  Timestamp,
  UpdateData,
  where,
  writeBatch,
} from 'firebase/firestore';
import { Observable, Observer, Subscription } from 'rxjs';
import { Dto, MAX_QUERY_RESULTS, Query, SearchResult } from './dtos';
import { ChangeTyped } from './firestore.dtos';
import { QueryService } from './query.service';

type Unsubscribe = () => void;

export const MAX_FIRESTORE_IN_LENGTH = 10;
export const MAX_FIRESTORE_BATCH_SIZE = 500;
export const ID_FIELD_OPTIONS: { idField: keyof Dto<string> } = { idField: '_id' };

export function mapSnapshotToDtos<T extends Dto<unknown>>(list: T[], items: ChangeTyped<T>[], unshiftItems = false): void {
  items.forEach((item) => {
    if (item.changeType === 'modified') {
      const index = list.findIndex((existingItem) => existingItem._id === item._id);
      if (index !== -1) {
        list[index] = item;
      }
    } else if (item.changeType === 'removed') {
      const index = list.findIndex((existingItem) => existingItem._id === item._id);
      if (index !== -1) {
        list.splice(index, 1);
      }
    } else {
      // Added or initial load
      if (unshiftItems) {
        list.unshift(item);
      } else {
        list.push(item);
      }
    }
  });
}

export function sanitizeTimestamps(dto: any): any {
  if (dto) {
    for (const p in dto) {
      // eslint-disable-next-line no-prototype-builtins
      if (dto.hasOwnProperty(p)) {
        if (dto[p] instanceof Timestamp) {
          dto[p] = (dto[p] as Timestamp).toDate();
        } else if (Array.isArray(dto[p])) {
          dto[p].forEach((v: any) => sanitizeTimestamps(v));
        } else if (typeof dto[p] === 'object') {
          sanitizeTimestamps(dto[p]);
        }
      }
    }
  }

  return dto;
}

export function dtoConverter<T>(extras: any = {}): FirestoreDataConverter<T> {
  return {
    toFirestore: (data: T) => data,
    fromFirestore: (snap): T => {
      return { ...sanitizeTimestamps(snap.data() as T), ...extras, _id: snap.id };
    },
  };
}

export async function firstCollectionData<T = DocumentData>(q: FirestoreQuery<T>): Promise<T[]> {
  const snap = await getDocs(q.withConverter(dtoConverter<T>()));
  return snap.docs.map((d) => d.data());
}

export function snapshotDtos<T extends Dto>(collectionRef: FirestoreQuery<T | DocumentData>, filterChangeType: DocumentChangeType[] = null): Observable<ChangeTyped<T>[]> {
  return new Observable((observer: Observer<ChangeTyped<T>[]>) => {
    const cancelListenerCallback = onSnapshot(
      collectionRef,
      (snap) => {
        const changes = snap.docChanges().reduce((list, change) => {
          if (!filterChangeType || filterChangeType?.includes(change.type)) {
            list.push(sanitizeTimestamps({ ...change.doc.data(), _id: change.doc.id, changeType: change.type }));
          }
          return list;
        }, []);

        observer.next(changes);
      },
      (e) => console.error(e, collectionRef),
    );

    return function unsubscribe(): void {
      cancelListenerCallback();
    };
  });
}

export const updateBatchFrom = async <T>(data: T[], pathBuilder: (d: T) => string): Promise<void> => {
  let batch = writeBatch(getFirestore());
  let i = 0;
  for (const d of data) {
    batch.update(doc(getFirestore(), pathBuilder(d)), d as UpdateData<unknown>);
    i++;

    if (i === MAX_FIRESTORE_BATCH_SIZE) {
      await batch.commit();
      batch = writeBatch(getFirestore());
      i = 0;
    }
  }

  if (i > 0) {
    await batch.commit();
  }
};

export const deleteBatchFrom = async <T>(data: T[], pathBuilder: (d: T) => string): Promise<void> => {
  let batch = writeBatch(getFirestore());
  let i = 0;
  for (const d of data) {
    batch.delete(doc(getFirestore(), pathBuilder(d)));
    i++;

    if (i === MAX_FIRESTORE_BATCH_SIZE) {
      await batch.commit();
      batch = writeBatch(getFirestore());
      i = 0;
    }
  }

  if (i > 0) {
    await batch.commit();
  }
};

export const createBatchFrom = async <T>(data: T[], docRefBuilder: (d: T) => DocumentReference): Promise<void> => {
  let batch = writeBatch(getFirestore());
  let i = 0;
  for (const d of data) {
    const newDocRef = docRefBuilder(d);
    batch.set(newDocRef, d as UpdateData<unknown>);
    i++;

    if (i === MAX_FIRESTORE_BATCH_SIZE) {
      await batch.commit();
      batch = writeBatch(getFirestore());
      i = 0;
    }
  }

  if (i > 0) {
    await batch.commit();
  }
};

export const updateBatch = async (col: string, data: Dto<unknown>[]): Promise<void> => {
  await updateBatchFrom(data, (d: Dto<unknown>) => `${col}/${d._id}`);
};

export const deleteBatch = async (col: string, data: Dto<unknown>[]): Promise<void> => {
  await deleteBatchFrom(data, (d: Dto<unknown>) => `${col}/${d._id}`);
};

export const createBatch = async (col: string, data: Dto<unknown>[]): Promise<void> => {
  await createBatchFrom(data, (d: Dto<unknown>) => {
    if (d._id) {
      return doc(getFirestore(), `${col}/${d._id}`);
    }

    return doc(collection(getFirestore(), col));
  });
};

export function convertQuery(q: Query, enforceLimit = false): QueryConstraint[] {
  const wheres: QueryConstraint[] = [];
  const filterKeys = Object.keys(q.filters || {});
  filterKeys.forEach((filterKey) => {
    const value = q.filters[filterKey];
    const isArray = Array.isArray(value);
    if (isArray && (value as unknown[]).length) {
      wheres.push(where(filterKey, 'in', value));
    } else if (!isArray) {
      wheres.push(where(filterKey, '==', value));
    }
  });

  if (q.filtersOps?.length) {
    q.filtersOps.forEach((f) => wheres.push(where(f.field, f.op, f.value)));
  }

  if (enforceLimit && q.size) {
    wheres.push(limit(q.size));
  }

  return wheres;
}

export async function searchPath<T>(firestore: Firestore, path: string, q: Query, searchFields?: string[], enforceLimit = false): Promise<SearchResult<T>> {
  const wheres = convertQuery(q, enforceLimit);
  const col = await getDocs(query(collection(firestore, path), ...wheres)).then((snaps: QuerySnapshot) => snaps.docs.map<T>(snapToDto));
  return QueryService.applyQuery<T>(col, q, searchFields);
}

export function snapToDto<T>(d: QueryDocumentSnapshot): T {
  return { ...d.data(), _id: d.id } as unknown as T;
}

export const queryChunker = async <T>(targetIds: string[], fieldTarget: string, executionMethod: (query: Query) => Promise<SearchResult<T>>): Promise<T[]> => {
  const data: T[] = [];
  const chunkSize = 10;
  for (let i = 0; i < targetIds.length; i += chunkSize) {
    const idChunk = targetIds.slice(i, i + chunkSize);
    const filter: Record<string, string[]> = {};
    filter[fieldTarget] = idChunk;
    const dataChunk = await executionMethod({ filters: filter, size: MAX_QUERY_RESULTS });
    if (dataChunk.result) {
      data.push(...dataChunk.result);
    }
  }
  return data;
};

export function unsubscribeToSubscription(unsub: Unsubscribe): Subscription {
  let unsubscribed = false;
  return {
    unsubscribe: () => {
      if (!unsubscribed) {
        unsubscribed = true;
        unsub();
      }
    },
    get closed() {
      return unsubscribed;
    },
  } as Subscription;
}
